/**
 * 统一筛选组件
 * 为所有需要筛选功能的页面提供一致的筛选界面
 */
import React, { useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Badge,
  Typography,
} from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Text } = Typography;

export interface FilterOption {
  label: string;
  value: string | number | boolean | undefined;
}

export interface FilterConfig {
  key: string;
  type: 'search' | 'select' | 'multiSelect' | 'dateRange' | 'custom';
  /** 参与“活跃筛选”计算的额外字段键（用于复合筛选） */
  activeKeys?: string[];
  label?: string;
  placeholder?: string | [string, string];
  options?: FilterOption[];
  span?: number; // 栅格占用列数
  allowClear?: boolean;
  customRender?: (
    value: any,
    onChange: (value: any) => void
  ) => React.ReactNode;
}

export interface UnifiedFiltersProps {
  filters: Record<string, any>;
  filterConfigs: FilterConfig[];
  onFiltersChange: (key: string, value: any) => void;
  onClearFilters: () => void;
  loading?: boolean;
  /** 变体：standalone 显示完整Card，embedded 只显示内容（用于嵌套在其他容器中） */
  variant?: 'standalone' | 'embedded';
}

export const UnifiedFilters: React.FC<UnifiedFiltersProps> = ({
  filters,
  filterConfigs,
  onFiltersChange,
  onClearFilters,
  loading = false,
  variant = 'standalone',
}) => {
  const { t } = useTranslation();
  const hasActiveValue = (value: any): boolean => {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  // 计算活跃筛选条件数量 - Bug修复: 只计算用户主动设置的筛选条件
  const activeFilterCount = useMemo(() => {
    return filterConfigs.filter(config => {
      const keysToCheck = [config.key, ...(config.activeKeys || [])];
      return keysToCheck.some(key => hasActiveValue(filters[key]));
    }).length;
  }, [filters, filterConfigs]);

  // 筛选项样式 - 有值时高亮
  const getFilterItemStyle = (key: string) => {
    const value = filters[key];
    const hasValue = hasActiveValue(value);

    return {
      transition: 'all 0.2s ease',
      ...(hasValue
        ? {
            background: 'rgba(16, 185, 129, 0.05)',
            borderRadius: '8px',
            padding: '8px',
            margin: '-8px',
          }
        : {}),
    };
  };

  const renderFilterItem = (config: FilterConfig) => {
    const {
      key,
      type,
      placeholder,
      options,
      allowClear = true,
      customRender,
    } = config;
    const value = filters[key];

    switch (type) {
      case 'search':
        return (
          <Search
            placeholder={placeholder as string}
            allowClear={allowClear}
            onSearch={val => onFiltersChange(key, val)}
            onChange={e => onFiltersChange(key, e.target.value)}
            value={value}
            style={{ width: '100%' }}
          />
        );

      case 'select':
        return (
          <Select
            placeholder={placeholder as string}
            allowClear={allowClear}
            style={{ width: '100%' }}
            value={value}
            onChange={val => onFiltersChange(key, val)}
            options={options}
          />
        );

      case 'multiSelect':
        return (
          <Select
            placeholder={placeholder as string}
            allowClear={allowClear}
            mode="multiple"
            suffixIcon={null}
            style={{ width: '100%' }}
            value={value}
            onChange={val => onFiltersChange(key, val)}
            options={options}
          />
        );

      case 'dateRange':
        return (
          <RangePicker
            placeholder={placeholder as [string, string]}
            style={{ width: '100%' }}
            value={value}
            onChange={dates => onFiltersChange(key, dates)}
          />
        );

      case 'custom':
        return customRender
          ? customRender(value, val => onFiltersChange(key, val))
          : null;

      default:
        return null;
    }
  };

  // 筛选内容区域（供两种变体共用）
  const filtersContent = (
    <Row gutter={[16, 16]}>
      {filterConfigs.map(config => (
        <Col
          key={config.key}
          xs={24}
          sm={12}
          md={config.span || 6}
          lg={config.span || 6}
        >
          <div style={getFilterItemStyle(config.key)}>
            {config.label && (
              <Text
                type="secondary"
                style={{
                  fontSize: '12px',
                  display: 'block',
                  marginBottom: '4px',
                  color: filters[config.key] ? '#10b981' : '#8c8c8c',
                  fontWeight: filters[config.key] ? 500 : 400,
                }}
              >
                {config.label}
              </Text>
            )}
            {renderFilterItem(config)}
          </div>
        </Col>
      ))}
    </Row>
  );

  // embedded 变体：只显示筛选内容和清除按钮
  if (variant === 'embedded') {
    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Space
            size="middle"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Badge
              count={activeFilterCount}
              size="small"
              color="#10b981"
              offset={[-2, 2]}
            >
              <FilterOutlined style={{ color: '#10b981', fontSize: '16px' }} />
            </Badge>
            <span
              style={{ color: '#065f46', fontWeight: 600, fontSize: '14px' }}
            >
              {t('screening:filter.smartFilterTitle')}
            </span>
            {activeFilterCount > 0 && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('screening:filter.selectedConditions', {
                  count: activeFilterCount,
                })}
              </Text>
            )}
          </Space>
          <Button
            size="small"
            icon={<ClearOutlined />}
            onClick={onClearFilters}
            disabled={activeFilterCount === 0}
            style={{
              borderColor: activeFilterCount > 0 ? '#10b981' : undefined,
              color: activeFilterCount > 0 ? '#10b981' : undefined,
            }}
          >
            {t('screening:filter.clear')}
          </Button>
        </div>
        {filtersContent}
      </div>
    );
  }

  // standalone 变体：完整Card显示
  return (
    <Card
      title={
        <Space size="middle" style={{ display: 'flex', alignItems: 'center' }}>
          <Badge
            count={activeFilterCount}
            size="small"
            color="#10b981"
            offset={[-2, 2]}
          >
            <FilterOutlined style={{ color: '#10b981', fontSize: '18px' }} />
          </Badge>
          <span style={{ color: '#065f46', fontWeight: 600, fontSize: '15px' }}>
            {t('screening:filter.smartFilterTitle')}
          </span>
          {activeFilterCount > 0 && (
            <Text type="secondary" style={{ fontSize: '13px' }}>
              {t('screening:filter.selectedConditions', {
                count: activeFilterCount,
              })}
            </Text>
          )}
        </Space>
      }
      extra={
        <Button
          icon={<ClearOutlined />}
          onClick={onClearFilters}
          disabled={activeFilterCount === 0}
          style={{
            borderColor: activeFilterCount > 0 ? '#10b981' : undefined,
            color: activeFilterCount > 0 ? '#10b981' : undefined,
          }}
        >
          {t('screening:filter.clearFilters')}
        </Button>
      }
      style={{
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(16, 185, 129, 0.1)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
        transition: 'all 0.3s ease',
        marginBottom: '24px',
      }}
      styles={{
        header: {
          background:
            'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
          borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
          borderRadius: '16px 16px 0 0',
          padding: '12px 24px',
          minHeight: '48px',
        },
        body: { padding: '16px 24px' },
      }}
    >
      {filtersContent}
    </Card>
  );
};
